import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Edit2 } from 'lucide-react';
import ProductImageEditor from './ProductImageEditor';

const ProductImageUploader = ({ existingImages = [], onUploadComplete }) => {
  const [images, setImages] = useState(() => existingImages.filter((u) => u && u.trim() !== ''));
  const [editorSrc, setEditorSrc] = useState(null); // dataURL da imagem em edição
  const [replaceIndex, setReplaceIndex] = useState(-1); // -1 = adicionar novo
  const inputRef = useRef(null);

  // Sync interno com o prop quando o admin muda de produto (edit → outro edit → novo).
  // Compara por conteúdo para não entrar em loop.
  useEffect(() => {
    const cleaned = (existingImages || []).filter((u) => u && u.trim() !== '');
    setImages((current) => {
      if (current.length === cleaned.length && current.every((u, i) => u === cleaned[i])) {
        return current;
      }
      return cleaned;
    });
  }, [existingImages]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Abre editor com a primeira imagem escolhida (uma por vez)
    const file = files[0];
    if (file.size > 20 * 1024 * 1024) {
      alert('Arquivo muito grande (máx. 20MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setEditorSrc(reader.result);
      setReplaceIndex(-1);
    };
    reader.readAsDataURL(file);
    e.target.value = ''; // permite reeleger mesmo arquivo depois
  };

  const handleEditorSave = ({ url }) => {
    let next;
    if (replaceIndex >= 0) {
      next = images.map((it, i) => (i === replaceIndex ? url : it));
    } else {
      next = [...images, url];
    }
    setImages(next);
    onUploadComplete(next);
    setEditorSrc(null);
    setReplaceIndex(-1);
  };

  const removeImage = (i) => {
    const next = images.filter((_, idx) => idx !== i);
    setImages(next);
    onUploadComplete(next);
  };

  const reeditImage = (i, url) => {
    setEditorSrc(url);
    setReplaceIndex(i);
  };

  return (
    <div data-testid="product-image-uploader">
      <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
        Imagens do Produto
      </label>
      <p className="text-xs text-muted-foreground mb-4">
        Cada imagem passa pelo editor antes de salvar. Corte, gire, aproxime — como no Canva.
      </p>

      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          {images.map((url, i) => (
            <div
              key={`${url}-${i}`}
              data-testid={`product-image-${i}`}
              className="relative aspect-square border border-border overflow-hidden group bg-neutral-50"
            >
              <img src={url} alt={`Imagem ${i + 1}`} className="w-full h-full object-contain p-2" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-opacity">
                <button
                  type="button"
                  onClick={() => reeditImage(i, url)}
                  className="bg-white text-neutral-900 p-2 hover:bg-neutral-100"
                  aria-label="Editar"
                  data-testid={`edit-image-${i}`}
                >
                  <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="bg-red-600 text-white p-2 hover:bg-red-700"
                  aria-label="Remover"
                  data-testid={`remove-image-${i}`}
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid="upload-product-image"
        className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-border hover:border-primary transition-all duration-300 p-8 cursor-pointer bg-transparent"
      >
        <Upload className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-sm text-muted-foreground">Adicionar imagem (edite antes de salvar)</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      <p className="text-xs text-muted-foreground mt-2">
        Formatos aceitos: JPG, PNG, WebP · Tamanho máx.: 20 MB
      </p>

      {editorSrc && (
        <ProductImageEditor
          imageSrc={editorSrc}
          onSave={handleEditorSave}
          onCancel={() => { setEditorSrc(null); setReplaceIndex(-1); }}
        />
      )}
    </div>
  );
};

export default ProductImageUploader;
